// src/components/Finanzas/TablaMovimientosCierre.jsx
// Tabla combinada de entregas + traslados pertenecientes a una relacion de
// gastos (cierre). Reemplaza la funcion inline `renderTablaMovimientos` que
// vivia en MiHistorialBolsillo.jsx y se reutiliza desde RevisionCierre.jsx.
//
// Opcionalmente acepta `onVerObservaciones(movimientoRaw, tipo)` para abrir
// el ModalObservacionesMovimiento. Si no se pasa, no se renderiza la columna
// de observaciones (compat con vistas read-only).

import React from 'react';
import { Table, Badge, Button } from 'react-bootstrap';
import { FaArrowDown, FaArrowUp, FaEye } from 'react-icons/fa';
import { formatoFechaColombia } from '../../utils/fechasColombia';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const tieneObservacionesTraslado = (t) =>
  !!(t?.confirmacion?.observaciones || t?.rechazo?.motivoRechazo);

const tieneObservacionesEntrega = (e) =>
  !!(e?.confirmacion?.observacionesReceptor || e?.rechazo?.motivoRechazo);

const TablaMovimientosCierre = ({
  entregas = [],
  traslados = [],
  bolsilloId,
  onVerObservaciones = null
}) => {
  const items = [
    ...entregas.map((e) => ({
      _id: `e-${e._id}`,
      raw: e,
      tipo: 'entrega',
      fecha: e.fecha,
      consecutivo: e.consecutivo,
      contraparte: e.cuentaOrigen?.nombre || e.entregadoPor?.nombre || 'Cuenta maestra',
      descripcion: e.descripcion,
      monto: e.monto,
      signo: '+',
      tieneObs: tieneObservacionesEntrega(e)
    })),
    ...traslados.map((t) => {
      const destinoId = String(t.cuenta_destino?._id || t.cuenta_destino || '');
      const esRecibido = destinoId === String(bolsilloId);
      return {
        _id: `t-${t._id}`,
        raw: t,
        tipo: 'traslado',
        fecha: t.fecha,
        consecutivo: t.consecutivo,
        contraparte: esRecibido
          ? (t.cuenta_origen?.nombre || '—')
          : (t.cuenta_destino?.nombre || '—'),
        descripcion: t.descripcion,
        monto: t.monto,
        signo: esRecibido ? '+' : '-',
        esRecibido,
        tieneObs: tieneObservacionesTraslado(t)
      };
    })
  ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

  if (!items.length) {
    return <p className="text-muted small mb-0">Sin movimientos en este cierre.</p>;
  }

  return (
    <Table hover size="sm" className="mb-0">
      <thead className="table-light">
        <tr>
          <th>Fecha</th>
          <th>Tipo</th>
          <th>Contraparte</th>
          <th>Descripción</th>
          <th className="text-end">Monto</th>
          {onVerObservaciones && <th className="text-center">Obs.</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((m) => (
          <tr key={m._id}>
            <td>{formatoFechaColombia(m.fecha)}</td>
            <td>
              {m.tipo === 'entrega' ? (
                <Badge bg="primary"><FaArrowDown className="me-1" />Entrega</Badge>
              ) : m.signo === '+' ? (
                <Badge bg="success"><FaArrowDown className="me-1" />Traslado recibido</Badge>
              ) : (
                <Badge bg="warning"><FaArrowUp className="me-1" />Traslado enviado</Badge>
              )}
            </td>
            <td><small>{m.contraparte}</small></td>
            <td><small>{m.descripcion || '—'}</small></td>
            <td className={`text-end fw-bold ${m.signo === '+' ? 'text-success' : 'text-warning'}`}>
              {m.signo}{formatoCOP(m.monto)}
            </td>
            {onVerObservaciones && (
              <td className="text-center">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  disabled={!m.tieneObs}
                  title={m.tieneObs ? 'Ver observaciones' : 'Sin observaciones'}
                  onClick={() => onVerObservaciones(m.raw, m.tipo)}
                >
                  <FaEye />
                </Button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default TablaMovimientosCierre;
